import catalog from "./dynamicBooksPublicPricingCatalog.json" with { type: "json" };

export type DynamicBooksPublicTierKey = "basic" | "business" | "premium";
// Keep this currency type and the SEK aliases compatible with existing billing consumers.
export type DynamicBooksBillingCurrency = "SEK";
export type DbkPricingCountry = "US" | "SE";
export type DbkPricingCurrency = "USD" | "SEK";
export type DbkBillingTierKey = `dynamicbooks_${DynamicBooksPublicTierKey}`;

export type DbkCountrySoftwareTier = {
  country_code: DbkPricingCountry;
  currency_code: DbkPricingCurrency;
  currency_minor_unit: 2;
  tier_key: DbkBillingTierKey;
  public_key: DynamicBooksPublicTierKey;
  display_name: "Basic" | "Business" | "Premium";
  amount_minor: number;
  approved: true;
  provider_lookup_key: string;
  provider_product_id: string;
  provider_price_id: string | null;
};

export const dbkPublicPricingCatalog = catalog;
export const dbkUsPublicSoftwareSeo = {
  title: "DynamicBooks US — accounting software and bookkeeping scope review",
  description: "US accounting software subscriptions in USD, with service work priced separately. Describe your bookkeeping needs and review a draft scope before choosing an engagement."
};
const countryTiers = catalog.tiers as readonly DbkCountrySoftwareTier[];

// Unknown financial countries fail closed; language fallback never selects a price book.
export function dbkListPublicSoftwareTiers(country: DbkPricingCountry): readonly DbkCountrySoftwareTier[] {
  if (country !== "US" && country !== "SE") {
    throw new Error("DynamicBooks software pricing country is unsupported.");
  }
  return countryTiers.filter((tier) => tier.country_code === country && tier.approved);
}

export function dbkGetPublicSoftwareTier(country: DbkPricingCountry, tierKey: DbkBillingTierKey): DbkCountrySoftwareTier {
  const tier = dbkListPublicSoftwareTiers(country).find((entry) => entry.tier_key === tierKey);
  if (!tier) throw new Error("DynamicBooks software tier is unavailable for this country.");
  return tier;
}

export function dbkBuildCountrySoftwareProductNodes(country: DbkPricingCountry, catalogUrl: string, origin: string): Array<Record<string, unknown>> {
  return dbkListPublicSoftwareTiers(country).map((tier) => {
    const price = tier.amount_minor / 10 ** tier.currency_minor_unit;
    return {
      "@type": "Product",
      "@id": new URL(`/#product-${tier.public_key}`, origin).toString(),
      name: `${tier.display_name} DynamicBooks`,
      description: "Monthly accounting software access. Service work, migration, onboarding, and Agent credits are priced separately.",
      category: "Accounting software",
      brand: { "@type": "Brand", name: "DynamicBooks" },
      url: catalogUrl,
      offers: {
        "@type": "Offer",
        "@id": new URL(`/#offer-${tier.public_key}`, origin).toString(),
        name: `${tier.display_name} DynamicBooks`,
        price,
        priceCurrency: tier.currency_code,
        url: catalogUrl,
        eligibleRegion: { "@type": "Country", name: tier.country_code },
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price,
          priceCurrency: tier.currency_code,
          unitText: "month before applicable tax",
          billingDuration: "P1M",
          valueAddedTaxIncluded: false
        }
      }
    };
  });
}

export function dbkBuildCountrySoftwarePricingGraph(country: DbkPricingCountry, origin: string): Array<Record<string, unknown>> {
  const catalogUrl = new URL("/#software-pricing", origin).toString();
  const products = dbkBuildCountrySoftwareProductNodes(country, catalogUrl, origin);
  return [
    { "@type": "OfferCatalog", "@id": new URL("/#software-tier-offers", origin).toString(),
      name: `DynamicBooks ${country} software subscriptions`, itemListElement: products.map((product) => product.offers) },
    ...products
  ];
}

export type DynamicBooksPublicSoftwareTier = {
  publicKey: DynamicBooksPublicTierKey;
  displayName: "Basic" | "Business" | "Premium";
  monthlyAmountOre: 14900 | 24900 | 39900;
  monthlyPriceSek: 149 | 249 | 399;
  currency: DynamicBooksBillingCurrency;
};

// The original public registry is the Sweden projection of the same catalog.
export const dynamicBooksPublicSoftwareTiers: readonly DynamicBooksPublicSoftwareTier[] = dbkListPublicSoftwareTiers("SE").map((tier) => ({
  publicKey: tier.public_key,
  displayName: tier.display_name,
  monthlyAmountOre: tier.amount_minor as DynamicBooksPublicSoftwareTier["monthlyAmountOre"],
  monthlyPriceSek: (tier.amount_minor / 100) as DynamicBooksPublicSoftwareTier["monthlyPriceSek"],
  currency: "SEK"
}));

export const dynamicBooksPublicSoftwareTierByKey = Object.fromEntries(
  dynamicBooksPublicSoftwareTiers.map((tier) => [tier.publicKey, tier])
) as Record<DynamicBooksPublicTierKey, DynamicBooksPublicSoftwareTier>;

export function listDynamicBooksPublicSoftwareTiers(): readonly DynamicBooksPublicSoftwareTier[] {
  return dynamicBooksPublicSoftwareTiers;
}
